import React, { useContext } from 'react'
import { connect } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import styled from '@emotion/styled'
import { buildPermissionedRoutes, flattenRoutes } from 'view/routes'
import { mainRoutes } from 'view/routes/mainRoutes'
import { SelectionActions } from 'store/selection'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { Application, Permission } from 'api'
import { Header } from './header/Header'
import { RoutesContent } from 'view/routes/RoutesContent'
import { LeftNavigation } from 'view/shared/leftNavigation'
import { ScreenWrapper } from 'view/routes/routesLayout'
import { EnvironmentContext } from 'view/shared/environment'
import { createLeftNavigationMinimisedSelector } from 'selectors/selectionSelectors'
import './mainContent.css'
import { SplitPanel } from './SplitPanel'

interface MainContentProps {
  isAdmin: boolean
  isLeftNavigationMinimised: boolean
  permissions: Permission[]
  setOrganisationId: (organisationId: string | null) => unknown
  clearAllCollectionIds: () => unknown
}

const mapStateToProps = createSelector(
  createAuthorisedUserSelector(),
  createLeftNavigationMinimisedSelector(),
  (authorisedUser, isLeftNavigationMinimised) => ({
    isAdmin: authorisedUser.application === Application.ADMIN_GATEWAY,
    permissions: authorisedUser.permissions,
    isLeftNavigationMinimised
  })
)

const mapDispatchToProps = {
  setOrganisationId: SelectionActions.setOrganisationId,
  clearAllCollectionIds: SelectionActions.clearAllCollectionIds
}

const MainContentWrapper = styled.main<{ isAdmin: boolean }>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: row;
  padding-bottom: 9px;
  height: ${props => (props.isAdmin ? 'calc(100% - 84px)' : 'calc(100% - 44px)')};
`

export const MainContentComponent: React.FunctionComponent<MainContentProps> = props => {
  const environment = useContext(EnvironmentContext)

  const { permissions, isLeftNavigationMinimised } = props
  const navigationRoutes = buildPermissionedRoutes(mainRoutes, permissions)
  const contentRoutes = flattenRoutes(navigationRoutes)

  return (
    <>
      <Header routes={contentRoutes} />
      <MainContentWrapper isAdmin={environment.isAdmin}>
        <SplitPanel>
          <LeftNavigation routes={navigationRoutes} isLeftNavigationMinimised={isLeftNavigationMinimised} />
          <ScreenWrapper hideRightMargin={environment.isTearout} data-test-id="MainScreen">
            <RoutesContent routes={contentRoutes} />
          </ScreenWrapper>
        </SplitPanel>
      </MainContentWrapper>
    </>
  )
}

export const MainContent = connect(mapStateToProps, mapDispatchToProps)(MainContentComponent)
